import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, XCircle } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { portalMoney } from "@/lib/portalApi";

const STATUS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/payments/status`;

export default function PortalPaymentReturn({ cancelled }) {
  const [params] = useSearchParams();
  const payaTx = params.get("paya_tx") || "";
  const sessionId = params.get("session_id") || payaTx;
  const statusUrl = payaTx ? `${STATUS_URL.replace("/status", "/paya/status")}` : STATUS_URL;
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled && !sessionId) setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${statusUrl}/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") {
          setState("failed");
          return;
        }
      } catch {
        if (tries >= 6) {
          setState("error");
          return;
        }
      }
      if (tries >= 6) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled, statusUrl]);

  const copy = {
    checking: {
      icon: Clock,
      tone: "text-zinc-500",
      title: "Checking your payment…",
      sub: "One moment while the bank confirms it.",
    },
    paid: {
      icon: CheckCircle,
      tone: "text-emerald-600",
      title: "Payment received — thank you",
      sub: tx?.amount
        ? `${portalMoney(tx.amount)} has been applied to ${tx.invoice_code}.`
        : "Your invoice has been updated.",
    },
    pending: {
      icon: Clock,
      tone: "text-amber-600",
      title: "Payment is still processing",
      sub: "We will update your invoice as soon as the bank confirms it.",
    },
    failed: {
      icon: XCircle,
      tone: "text-red-600",
      title: "That payment did not go through",
      sub: "Nothing was charged. You can try again from the invoice.",
    },
    cancelled: {
      icon: XCircle,
      tone: "text-zinc-500",
      title: "Payment cancelled",
      sub: "Nothing was charged. Your invoice is still waiting when you are ready.",
    },
    error: {
      icon: XCircle,
      tone: "text-red-600",
      title: "We could not confirm that payment",
      sub: "Please call the shop and we will check it for you.",
    },
  }[state];

  const Icon = copy.icon;

  return (
    <PortalShell>
      <div
        className="mx-auto mt-12 max-w-lg rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center"
        data-testid="portal-payment-return"
      >
        <Icon size={40} weight="bold" className={`mx-auto ${copy.tone}`} />
        <h1
          className="mt-4 text-2xl font-semibold tracking-tight text-zinc-100"
          data-testid="portal-payment-title"
        >
          {copy.title}
        </h1>
        <p className="mt-2 text-sm text-zinc-500">{copy.sub}</p>
        <Link
          to="/portal/home"
          data-testid="portal-payment-home"
          className="mt-6 inline-block rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Back to my projects
        </Link>
      </div>
    </PortalShell>
  );
}
